'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
} from '@/schemas/resetPasswordSchema';
import { userService } from '@/api/user/userApi';
import { handleGlobalError } from '@/utils/error-handler';

const ResetPassword: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const watchedFields = watch();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      toast({
        title: 'Token inválido',
        description: 'El enlace de recuperación no es válido o ha expirado.',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }
    setToken(tokenFromUrl);
  }, [searchParams, navigate, toast]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    try {
      await userService.confirmPasswordReset(token, data.newPassword);
      setIsSuccess(true);
      toast({
        title: 'Contraseña actualizada',
        description: 'Tu contraseña ha sido cambiada exitosamente.',
      });
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      handleGlobalError(
        error,
        'Error al cambiar contraseña',
        'No se pudo cambiar la contraseña. El enlace podría haber expirado.',
      );
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <img
                src="/assets/images/logo.svg"
                alt="GetMatch Logo"
                className="mx-auto mb-4 h-20 w-auto"
              />
              <h2 className="font-gilroy text-2xl font-bold text-primary">
                ¡Contraseña Actualizada!
              </h2>
            </div>

            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-opensans text-sm leading-relaxed text-gray-700">
                  Tu contraseña ha sido cambiada exitosamente.
                </p>
                <p className="mt-3 font-opensans text-sm text-gray-600">
                  Serás redirigido al inicio de sesión en unos segundos...
                </p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full rounded bg-secondary py-3 font-gilroy font-bold text-white transition duration-200 hover:bg-secondary/90"
              >
                Ir al inicio de sesión
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="mb-2 font-gilroy text-xl font-bold text-primary">
                Enlace Inválido
              </h2>
              <p className="mb-6 font-opensans text-sm text-gray-600">
                El enlace de recuperación no es válido o ha expirado.
              </p>
              <Link
                to="/forgot-password"
                className="font-opensans text-sm text-secondary hover:underline"
              >
                Solicitar nuevo enlace
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary">
      <Card className="w-full max-w-md bg-white shadow-lg">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <img
              src="/assets/images/logo.svg"
              alt="GetMatch Logo"
              className="mx-auto mb-4 h-20 w-auto"
            />
            <h2 className="font-gilroy text-2xl font-bold text-primary">
              Nueva Contraseña
            </h2>
            <p className="mt-2 font-opensans text-sm text-gray-600">
              Ingresa tu nueva contraseña para completar la recuperación.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="newPassword"
                className="font-opensans text-sm font-semibold text-primary"
              >
                Nueva contraseña
              </Label>
              <PasswordInput
                id="newPassword"
                {...register('newPassword')}
                autoComplete="new-password"
                className="border-accent focus:border-secondary focus:ring-secondary"
                disabled={isSubmitting}
                placeholder="Mínimo 6 caracteres"
              />
              {errors.newPassword && (
                <p className="font-opensans text-sm text-red-600">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="font-opensans text-sm font-semibold text-primary"
              >
                Confirmar nueva contraseña
              </Label>
              <PasswordInput
                id="confirmPassword"
                {...register('confirmPassword')}
                autoComplete="new-password"
                className="border-accent focus:border-secondary focus:ring-secondary"
                disabled={isSubmitting}
                placeholder="Repite la contraseña"
              />
              {errors.confirmPassword && (
                <p className="font-opensans text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded bg-secondary py-3 font-gilroy font-bold text-white transition duration-200 hover:bg-secondary/90"
              disabled={
                isSubmitting ||
                !isValid ||
                !watchedFields.newPassword ||
                !watchedFields.confirmPassword
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cambiando contraseña...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Cambiar contraseña
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
